import {InfoCircleOutlined} from "@ant-design/icons";
import {Button, Form, Input, message, Space} from "antd";
import classNames from "classnames";
import {Formik} from "formik";
import React, {FC, memo} from "react";
import NotFound from "../../containers/common/NotFound";
import {
    EditBookingInput,
    GetBookingByIdQuery,
    GetBookingByIdWithCourseLessonBookingsDocument,
    GetBookingByIdWithCourseLessonBookingsQuery,
    useUpdateNoticeStaffMutation,
} from "../../generated/graphql";
import {createInputHelpers} from "../../helpers/createInputHelpers";
import {isDefined} from "../../helpers/typeScriptHelpers";
import i18n from "../../services/i18n";
import PanelCard from "../PanelCard/PanelCard";
import css from "./PanelCardNotes.less";
import {erasedRecordMessage} from "../../helpers/erasedRecordMessage";

type PosibleBookingType = GetBookingByIdQuery["bookingById"] | GetBookingByIdWithCourseLessonBookingsQuery["bookingByIdWithCourseLessonBookings"];

type PanelCardNotesProps = {
    // booking: GetBookingByIdQuery["bookingById"];
    booking: PosibleBookingType;
    style?: React.CSSProperties;
};

const PanelCardNotes: FC<PanelCardNotesProps> = ({booking, style}) => {
    const [updateNoticeStaff, {loading}] = useUpdateNoticeStaffMutation();

    type ContactCardCSSProperties = React.CSSProperties & {
        "--column-count": number;
    };

    const actualStyle: ContactCardCSSProperties = {
        "--column-count": 1,
        ...style,
    };

    if (booking === undefined) {
        return <NotFound />;
    }

    return (
        <Formik<EditBookingInput>
            initialValues={booking as EditBookingInput}
            // validationSchema={EditBookingInput}
            onSubmit={async (values, {setSubmitting}) => {
                try {
                    await updateNoticeStaff({
                        variables: {
                            id: booking.id,
                            noticeStaff: values.noticeStaff ?? "",
                        },
                        refetchQueries: [
                            "GetBookingsOverviewOptimized",
                            "GetUnsettledBookings",
                            "GetSettledBookings",
                            "GetBookingByIdWithCourseLessonBookings",
                            {query: GetBookingByIdWithCourseLessonBookingsDocument, variables: {id: booking.id}},
                            // "GetBookingsByCourse",
                            // {query: GET_BOOKINGS_BY_COURSE},
                        ],
                        awaitRefetchQueries: true,
                    }).then((res) => {
                        message.success(i18n.containers.invoice.InvoiceDetails.message.bookingUpdated());
                    });
                } catch (error) {
                    message.error(erasedRecordMessage(error) ?? i18n.containers.invoice.InvoiceDetails.message.bookingUpdateError());
                    console.error(error);
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            {(formikProps) => {
                const {values, submitForm} = formikProps;
                const {help, handleBlur, handleTextInputChange} = createInputHelpers<EditBookingInput>(formikProps);

                return (
                    <PanelCard columns={2} title={i18n.containers.invoice.InvoiceDetails.notes()} gapAfter>
                        <>
                            <div className={classNames(css.withDivider, css.container)} style={actualStyle}>
                                <div className={css.label}>{"Notiz BucherIn"}</div>
                                <div className={css.entry}>
                                    <Form.Item help={help("noticeBooker")}>
                                        <Input.TextArea className={css.textArea} rows={2} disabled value={isDefined(values.noticeBooker) ? values.noticeBooker : ""} />
                                    </Form.Item>
                                </div>
                                <Space className={css.hint}>
                                    <InfoCircleOutlined />
                                    {"Notizen des Buchers können nicht geändert werden."}
                                </Space>
                            </div>
                            <div className={classNames(css.withDivider, css.container)} style={actualStyle}>
                                <div className={css.label}>{"Notiz Mitarbeiterin zur Buchung"}</div>
                                <div className={css.entry}>
                                    <Form.Item help={help("noticeStaff")}>
                                        <Input.TextArea
                                            className={css.textArea}
                                            rows={2}
                                            value={isDefined(values.noticeStaff) ? values.noticeStaff : ""}
                                            onChange={handleTextInputChange("noticeStaff")}
                                            onBlur={handleBlur("noticeStaff")}
                                        />
                                    </Form.Item>
                                    <Button htmlType="submit" onClick={submitForm} loading={loading} type={booking === values ? "default" : "primary"} className={css.submitButton}>
                                        {i18n.containers.invoice.InvoiceDetails.saveNote()}
                                    </Button>
                                </div>
                            </div>
                        </>
                    </PanelCard>
                );
            }}
        </Formik>
    );
};

export default memo(PanelCardNotes);
